import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stringify } from 'query-string';
import type { LinkInBio, LinkInBioPageSummary } from '@repo/lib';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import type { LinkInBioAnalyticsResponse } from '@repo/schemas';
import { linkInBioAnalyticsKey, linkInBioKey, linkInBioPagesKey } from '@dashboard/lib/queryKeys';

export type LinkInBioResponse = {
  linkInBio: LinkInBio;
  pages: LinkInBioPageSummary[];
  upcomingEvents: LinkInBioPageSummary[];
};

export function useLinkInBio({ enabled = true }: { enabled?: boolean } = {}) {
  const { sellerId, token } = useAuth();

  return useQuery<LinkInBioResponse>({
    queryKey: linkInBioKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}/link-in-bio`, token),
    enabled: enabled && !!sellerId,
  });
}

export function useLinkInBioPages(ids: string[]) {
  const { sellerId, token } = useAuth();
  const sortedIds = [...ids].sort();

  return useQuery<{ pages: LinkInBioPageSummary[] }>({
    queryKey: linkInBioPagesKey(sellerId, sortedIds),
    queryFn: () =>
      fetcher(`/sellers/${sellerId}/link-in-bio/pages?ids=${sortedIds.join(',')}`, token),
    enabled: !!sellerId && sortedIds.length > 0,
    staleTime: 60_000,
  });
}

export function useSaveLinkInBio({
  onSuccess,
}: { onSuccess?: (data: LinkInBioResponse) => void } = {}) {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: LinkInBio): Promise<LinkInBioResponse> =>
      fetcher(`/sellers/${sellerId}/link-in-bio`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      }),
    onSuccess: data => {
      queryClient.setQueryData(linkInBioKey(sellerId), data);
      onSuccess?.(data);
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Could not save your link in bio',
        description: error?.message || 'Check the highlighted fields and try again',
        type: 'error',
      });
    },
  });
}

export type LinkInBioAnalyticsQuery = {
  dateStart?: string;
  dateEnd: string;
  unit: string;
  timezoneOffset: string;
};

export function useLinkInBioAnalytics(
  query: LinkInBioAnalyticsQuery,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const { sellerId, token } = useAuth();

  return useQuery<LinkInBioAnalyticsResponse>({
    queryKey: linkInBioAnalyticsKey(sellerId, query),
    queryFn: () => fetcher(`/sellers/${sellerId}/link-in-bio/analytics?${stringify(query)}`, token),
    enabled: enabled && !!sellerId,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}
